// Use env override first, then safe defaults for dev/prod hosts
const resolveApiUrl = () => {
  const fromEnv = (process.env.REACT_APP_API_URL || '').trim();
  if (fromEnv) return fromEnv.replace(/\/$/, '');

  const host = window.location.hostname;
  const isLocalHost = host === 'localhost' || host === '127.0.0.1';
  return isLocalHost ? 'http://localhost:5001/api' : '/api';
};

const API_URL = resolveApiUrl();

class ApiService {
  constructor() {
    this.token = localStorage.getItem('token');
    this.refreshPromise = null;
    this.authFailureBroadcasted = false;
  }

  normalizeErrorMessage(message, status = 0) {
    const raw = String(message || '').trim();
    const lower = raw.toLowerCase();
    if (!raw) {
      if (status === 401) return 'Your session has expired. Please sign in again.';
      if (status === 403) return 'You do not have permission to perform this action.';
      if (status === 404) return 'The requested item was not found.';
      if (status >= 500) return 'Server issue. Please try again in a moment.';
      return 'Request failed. Please try again.';
    }
    if (lower.includes('database error')) {
      return 'Server issue while saving data. Please try again.';
    }
    if (
      lower.includes('unique constraint failed')
      || lower.includes('already exists')
      || lower.includes('duplicate')
    ) {
      if (lower.includes('order_number') || lower.includes('order number')) {
        return 'Order number already exists. Use the suggested next order number.';
      }
      return 'This value already exists. Please use a different value.';
    }
    return raw;
  }

  dispatchAuthChanged() {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app:auth-changed'));
    }
  }

  setToken(token) {
    this.token = token || null;
    this.authFailureBroadcasted = false;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  setRefreshToken(token) {
    if (token) {
      localStorage.setItem('refresh_token', token);
    } else {
      localStorage.removeItem('refresh_token');
    }
  }

  setCurrentUser(user) {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }

  setAuthSession({ token, refreshToken, user, broadcast = true } = {}) {
    if (token !== undefined) this.setToken(token);
    if (refreshToken !== undefined) this.setRefreshToken(refreshToken);
    if (user !== undefined) this.setCurrentUser(user);
    if (broadcast) this.dispatchAuthChanged();
  }

  getToken() {
    return this.token || localStorage.getItem('token');
  }

  getRefreshToken() {
    return localStorage.getItem('refresh_token');
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('token');
  }

  clearSession({ broadcast = true } = {}) {
    this.clearToken();
    localStorage.removeItem('user');
    localStorage.removeItem('refresh_token');
    if (broadcast) this.dispatchAuthChanged();
  }

  handleAuthFailure(status, message = '') {
    const normalized = String(message || '').toLowerCase();
    const isExpiredOrInvalidToken = status === 401 || (status === 403 && normalized.includes('invalid token'));
    if (!isExpiredOrInvalidToken) return;
    this.clearSession({ broadcast: true });
    if (this.authFailureBroadcasted) return;
    this.authFailureBroadcasted = true;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app:auth-expired'));
    }
  }

  async request(endpoint, options = {}) {
    const {
      skipAuthRefresh = false,
      suppressAuthFailure = false,
      ...fetchOptions
    } = options;
    if (!navigator.onLine) {
      throw new Error('Device is offline. Please reconnect and try again.');
    }
    const token = this.getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...fetchOptions.headers,
    };

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
    });

    const isJson = (response.headers.get('content-type') || '').includes('application/json');
    let payload = null;
    if (isJson) {
      try {
        payload = await response.json();
      } catch (_err) {
        payload = null;
      }
    } else {
      try {
        payload = await response.text();
      } catch (_err) {
        payload = '';
      }
    }

    if (!response.ok) {
      const serverMessage = isJson
        ? (payload?.error || payload?.message || '')
        : String(payload || '');
      const normalizedMessage = String(serverMessage || '').toLowerCase();
      const isExpiredOrInvalidToken = response.status === 401 || (response.status === 403 && normalizedMessage.includes('invalid token'));
      if (isExpiredOrInvalidToken && !skipAuthRefresh && this.getRefreshToken()) {
        try {
          await this.refreshSession();
          return this.request(endpoint, {
            ...options,
            skipAuthRefresh: true
          });
        } catch (_refreshErr) {
          // Fall through to the normal auth-failure path below.
        }
      }
      if (!suppressAuthFailure) {
        this.handleAuthFailure(response.status, serverMessage);
      }
      const error = new Error(this.normalizeErrorMessage(serverMessage, response.status));
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    return isJson ? payload : {};
  }

  // Auth
  async login(username, password) {
    const data = await this.request('/login', {
      method: 'POST',
      skipAuthRefresh: true,
      suppressAuthFailure: true,
      body: JSON.stringify({ username, password }),
    });
    this.setAuthSession({
      token: data.token,
      refreshToken: data.refresh_token,
      user: data.user
    });
    return data;
  }

  logout() {
    const refreshToken = this.getRefreshToken();
    this.clearSession({ broadcast: true });
    this.authFailureBroadcasted = false;
    if (refreshToken && navigator.onLine) {
      fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      }).catch(() => {});
    }
  }

  getCurrentUser() {
    const user = localStorage.getItem('user');
    if (!user) return null;
    try {
      return JSON.parse(user);
    } catch (_err) {
      this.clearSession({ broadcast: false });
      return null;
    }
  }

  async refreshSession() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh session available.');
    }
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      if (!navigator.onLine) {
        throw new Error('Device is offline. Please reconnect and try again.');
      }
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
      const isJson = (response.headers.get('content-type') || '').includes('application/json');
      const payload = isJson ? await response.json() : {};
      if (!response.ok) {
        const serverMessage = payload?.error || payload?.message || '';
        const error = new Error(this.normalizeErrorMessage(serverMessage, response.status));
        error.status = response.status;
        error.payload = payload;
        throw error;
      }
      this.setAuthSession({
        token: payload.token,
        refreshToken: payload.refresh_token,
        user: payload.user
      });
      return payload;
    })();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  async getAuthMe(options = {}) {
    return this.request('/auth/me', options);
  }

  async getMyPermissions(options = {}) {
    return this.request('/me/permissions', options);
  }

  async restoreSession() {
    const currentUser = this.getCurrentUser();
    const hasToken = Boolean(this.getToken());
    const hasRefreshToken = Boolean(this.getRefreshToken());

    if (!currentUser && !hasToken && !hasRefreshToken) {
      return null;
    }

    if (!navigator.onLine) {
      return currentUser;
    }

    try {
      if (!hasToken && hasRefreshToken) {
        await this.refreshSession();
      }
      const data = await this.getAuthMe({ suppressAuthFailure: true });
      if (data?.user) {
        this.setCurrentUser(data.user);
        return data.user;
      }
      return currentUser;
    } catch (_err) {
      this.clearSession({ broadcast: false });
      return null;
    }
  }

  // Colors
  async getColors(options = {}) {
    return this.request('/colors', options);
  }

  async addColor(name) {
    return this.request('/colors', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updateColor(id, name) {
    return this.request(`/colors/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  }

  async deleteColor(id) {
    return this.request(`/colors/${id}`, {
      method: 'DELETE',
    });
  }

  // Yarn Types
  async getYarnTypes() {
    return this.request('/yarn-types');
  }

  async addYarnType(name) {
    return this.request('/yarn-types', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updateYarnType(id, name) {
    return this.request(`/yarn-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  }

  async deleteYarnType(id) {
    return this.request(`/yarn-types/${id}`, {
      method: 'DELETE',
    });
  }

  // Transactions
  async getTransactions(materialType = 'custom', options = {}) {
    return this.request(`/transactions?material_type=${encodeURIComponent(materialType)}`, options);
  }

  async addTransaction(transaction) {
    return this.request('/transactions', {
      method: 'POST',
      body: JSON.stringify(transaction),
    });
  }

  async updateTransaction(id, transaction) {
    return this.request(`/transactions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(transaction),
    });
  }

  async updateTransactionNotes(id, notes) {
    return this.request(`/transactions/${id}/notes`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    });
  }

  async deleteTransaction(id) {
    return this.request(`/transactions/${id}`, {
      method: 'DELETE',
    });
  }

  async previewTransactionsImport({
    csv_text,
    mode = 'transactions',
    inventory_kind = 'raw_material',
    allow_negative = false,
    auto_add_characteristics = false,
    default_raw_material = '',
    default_date = '',
    skip_invalid = false
  }) {
    return this.request('/transactions/import-preview', {
      method: 'POST',
      body: JSON.stringify({ csv_text, mode, inventory_kind, allow_negative, auto_add_characteristics, default_raw_material, default_date, skip_invalid }),
    });
  }

  async importTransactionsCsv({
    csv_text,
    mode = 'transactions',
    inventory_kind = 'raw_material',
    allow_negative = false,
    auto_add_characteristics = false,
    default_raw_material = '',
    default_date = '',
    skip_invalid = false
  }) {
    return this.request('/transactions/import', {
      method: 'POST',
      body: JSON.stringify({ csv_text, mode, inventory_kind, allow_negative, auto_add_characteristics, default_raw_material, default_date, skip_invalid }),
    });
  }

  async getChallans(params = {}) {
    const query = new URLSearchParams();
    if (params.type) query.set('type', params.type);
    if (params.status) query.set('status', params.status);
    if (params.q) query.set('q', params.q);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request(`/challans${suffix}`);
  }

  async cancelChallan(id, reason = '') {
    return this.request(`/challans/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async reissueChallan(id, payload = {}) {
    return this.request(`/challans/${id}/reissue`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getChallanPrintHtml(id, printPreferences = null) {
    const token = this.getToken();
    const query = new URLSearchParams();
    const prefs = printPreferences && typeof printPreferences === 'object' ? printPreferences : {};
    if (prefs.layout) query.set('layout', prefs.layout);
    if (prefs.include_unit_cost !== undefined) query.set('include_unit_cost', String(Boolean(prefs.include_unit_cost)));
    if (prefs.include_estimated_cost !== undefined) query.set('include_estimated_cost', String(Boolean(prefs.include_estimated_cost)));
    if (prefs.include_product_id !== undefined) query.set('include_product_id', String(Boolean(prefs.include_product_id)));
    if (prefs.include_order_ids !== undefined) query.set('include_order_ids', String(Boolean(prefs.include_order_ids)));
    if (prefs.include_qr !== undefined) query.set('include_qr', String(Boolean(prefs.include_qr)));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    const response = await fetch(`${API_URL}/challans/${id}/print${suffix}`, {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    const text = await response.text();
    if (!response.ok) {
      const error = new Error(this.normalizeErrorMessage(text, response.status));
      error.status = response.status;
      throw error;
    }
    return text;
  }

  async downloadChallanPdf(id) {
    const token = this.getToken();
    const response = await fetch(`${API_URL}/challans/${id}/pdf`, {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      const text = await response.text();
      const error = new Error(this.normalizeErrorMessage(text, response.status));
      error.status = response.status;
      throw error;
    }
    return response.blob();
  }

  // Stock
  async getStock(options = {}) {
    return this.request('/stock', options);
  }

  async getRawMaterialStock(materialType, options = {}) {
    return this.request(`/raw-material-stock?material_type=${encodeURIComponent(materialType)}`, options);
  }

  // Move a characteristic "type" value from one raw material to another.
  async previewMoveRawMaterialType(body) {
    return this.request('/raw-materials/move-type/preview', { method: 'POST', body: JSON.stringify(body) });
  }

  async moveRawMaterialType(body) {
    return this.request('/raw-materials/move-type', { method: 'POST', body: JSON.stringify(body) });
  }

  // Users
  async getUsers(options = {}) {
    return this.request('/users', options);
  }

  async addUser(username, password, role) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify({ username, password, role }),
    });
  }

  async updateUser(id, username, password, role) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ username, password, role }),
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  async setUserStatus(id, isActive) {
    return this.request(`/users/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: isActive }),
    });
  }

  async forceUserPasswordReset(id) {
    return this.request(`/users/${id}/force-password-reset`, {
      method: 'POST',
    });
  }

  // Staff
  async getStaff(options = {}) {
    return this.request('/staff', options);
  }

  async addStaff(payload) {
    return this.request('/staff', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateStaff(id, payload) {
    return this.request(`/staff/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async updateStaffNotes(id, notes) {
    return this.request(`/staff/${id}/notes`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    });
  }

  async deleteStaff(id) {
    return this.request(`/staff/${id}`, {
      method: 'DELETE',
    });
  }

  async anonymizeStaff(id) {
    return this.request(`/staff/${id}/anonymize`, {
      method: 'POST',
    });
  }

  async updateStaffStatus(id, isActive) {
    return this.request(`/staff/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: isActive }),
    });
  }

  async revealStaffId(id) {
    return this.request(`/staff/${id}/reveal`);
  }

  async getStaffAttendance(date, options = {}) {
    return this.request(`/staff-attendance?date=${encodeURIComponent(String(date || '').trim())}`, options);
  }

  async getStaffAttendanceRange(start, end, options = {}) {
    const qs = `start=${encodeURIComponent(String(start || '').trim())}&end=${encodeURIComponent(String(end || '').trim())}`;
    return this.request(`/staff-attendance/range?${qs}`, options);
  }

  async saveStaffAttendance(date, entries) {
    return this.request('/staff-attendance/bulk', {
      method: 'PUT',
      body: JSON.stringify({ date, entries }),
    });
  }

  // Customers
  async getInspections(options = {}) {
    return this.request('/inspections', options);
  }

  async createInspection(payload) {
    return this.request('/inspections', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async releaseInspection(id) {
    return this.request(`/inspections/${id}/release`, { method: 'PATCH' });
  }

  async voidInspection(id, reason) {
    return this.request(`/inspections/${id}/void`, {
      method: 'PATCH',
      body: JSON.stringify({ reason }),
    });
  }

  async updateInspection(id, payload) {
    return this.request(`/inspections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async getCustomers(options = {}) {
    return this.request('/customers', options);
  }

  async addCustomer(payload) {
    return this.request('/customers', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateCustomer(id, payload) {
    return this.request(`/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async updateCustomerNotes(id, notes) {
    return this.request(`/customers/${id}/notes`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    });
  }

  async deleteCustomer(id) {
    return this.request(`/customers/${id}`, {
      method: 'DELETE',
    });
  }

  async getContractors(options = {}) {
    return this.request('/contractors', options);
  }

  async addContractor(payload) {
    return this.request('/contractors', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateContractor(id, payload) {
    return this.request(`/contractors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async updateContractorNotes(id, notes) {
    return this.request(`/contractors/${id}/notes`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    });
  }

  async deleteContractor(id) {
    return this.request(`/contractors/${id}`, {
      method: 'DELETE',
    });
  }

  async getMachines(options = {}) {
    return this.request('/machines', options);
  }

  async addMachine(payload) {
    return this.request('/machines', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateMachine(id, payload) {
    return this.request(`/machines/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async setMachineStatus(id, status) {
    return this.request(`/machines/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async deleteMachine(id) {
    return this.request(`/machines/${id}`, {
      method: 'DELETE',
    });
  }

  async assignOrderMachine(orderId, machineId) {
    return this.request(`/orders/${orderId}/machine`, {
      method: 'PUT',
      body: JSON.stringify({ assigned_machine_id: machineId }),
    });
  }

  async getMachineMaintenance(machineId) {
    return this.request(`/machines/${machineId}/maintenance`);
  }

  async addMachineMaintenance(machineId, payload) {
    return this.request(`/machines/${machineId}/maintenance`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteMachineMaintenance(machineId, recordId) {
    return this.request(`/machines/${machineId}/maintenance/${recordId}`, {
      method: 'DELETE',
    });
  }

  async addMachinePmSchedule(machineId, payload) {
    return this.request(`/machines/${machineId}/pm-schedules`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateMachinePmSchedule(machineId, scheduleId, payload) {
    return this.request(`/machines/${machineId}/pm-schedules/${scheduleId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteMachinePmSchedule(machineId, scheduleId) {
    return this.request(`/machines/${machineId}/pm-schedules/${scheduleId}`, {
      method: 'DELETE',
    });
  }

  async getMachineRunlog(machineId) {
    return this.request(`/machines/${machineId}/runlog`);
  }

  async addMachineRunlog(machineId, payload) {
    return this.request(`/machines/${machineId}/runlog`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateMachineRunlog(machineId, logId, payload) {
    return this.request(`/machines/${machineId}/runlog/${logId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteMachineRunlog(machineId, logId) {
    return this.request(`/machines/${machineId}/runlog/${logId}`, {
      method: 'DELETE',
    });
  }

  async getMachineAnalytics(machineId, days = 30) {
    return this.request(`/machines/${machineId}/analytics?days=${days}`);
  }

  async getMachinesFleetAnalytics(days = 30, options = {}) {
    return this.request(`/machines/fleet-analytics?days=${days}`, options);
  }

  async createContactLead(payload) {
    return this.request('/contact-leads', {
      method: 'POST',
      body: JSON.stringify(payload),
      skipAuthRefresh: true,
      suppressAuthFailure: true
    });
  }

  async trackWebsiteEvent(payload) {
    return this.request('/website-events', {
      method: 'POST',
      body: JSON.stringify(payload),
      skipAuthRefresh: true,
      suppressAuthFailure: true
    });
  }

  // Companies (super admin)
  async getCompanies() {
    return this.request('/companies');
  }

  async getEmailStatus() {
    return this.request('/admin/email-status');
  }

  async getCompanyTemplate(id) {
    return this.request(`/companies/${id}/template`);
  }

  async getCompanyLabels(id) {
    return this.request(`/companies/${id}/labels`);
  }

  async getCustomLists(options = {}) {
    return this.request('/custom-lists', options);
  }

  async createCustomList(list_key, label) {
    return this.request('/custom-lists', {
      method: 'POST',
      body: JSON.stringify({ list_key, label }),
    });
  }

  async addCustomListItem(listKey, value) {
    return this.request(`/custom-lists/${listKey}/items`, {
      method: 'POST',
      body: JSON.stringify({ value }),
    });
  }

  async updateCustomListItem(id, value) {
    return this.request(`/custom-lists/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  }

  async deleteCustomListItem(id) {
    return this.request(`/custom-lists/items/${id}`, {
      method: 'DELETE',
    });
  }

  async createCompany({ name, slug, timezone }) {
    return this.request('/companies', {
      method: 'POST',
      body: JSON.stringify({ name, slug, timezone }),
    });
  }

  async createCompanySetup(formData) {
    const token = this.getToken();
    const response = await fetch(`${API_URL}/companies/setup`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });
    const isJson = (response.headers.get('content-type') || '').includes('application/json');
    let payload = null;
    if (isJson) {
      try {
        payload = await response.json();
      } catch (_err) {
        payload = null;
      }
    } else {
      try {
        payload = await response.text();
      } catch (_err) {
        payload = '';
      }
    }
    if (!response.ok) {
      const serverMessage = isJson
        ? (payload?.error || payload?.message || '')
        : String(payload || '');
      throw new Error(this.normalizeErrorMessage(serverMessage, response.status));
    }
    return isJson ? payload : {};
  }

  async deleteCompany(id) {
    return this.request(`/companies/${id}`, {
      method: 'DELETE',
    });
  }

  async updateCompanyStatus(id, isActive) {
    return this.request(`/companies/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ is_active: isActive }),
    });
  }

  async updateCompanyUserLimit(id, userLimit) {
    return this.request(`/companies/${id}/user-limit`, {
      method: 'PATCH',
      body: JSON.stringify({ user_limit: userLimit }),
    });
  }

  async updateCompanyBatchTracking(id, enabled) {
    return this.request(`/companies/${id}/batch-tracking`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    });
  }

  async updateCompanyUomConversion(id, enabled) {
    return this.request(`/companies/${id}/uom-conversion`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    });
  }

  // schemas: { customer_schema?, staff_schema?, contractor_schema? }, each { fields: [...] }
  async updateCompanyPeopleSchemas(id, schemas) {
    return this.request(`/companies/${id}/people-schemas`, {
      method: 'PATCH',
      body: JSON.stringify({ schemas }),
    });
  }

  async updateCompanySlug(id, slug) {
    return this.request(`/companies/${id}/slug`, {
      method: 'PATCH',
      body: JSON.stringify({ slug }),
    });
  }

  async getUserLimit() {
    return this.request('/users/limit');
  }

  // Build/version metadata for the server instance this client is talking to.
  // Unauthenticated; never trip the auth-refresh/logout flow on a failure.
  async getServerVersion() {
    return this.request('/version', { skipAuthRefresh: true, suppressAuthFailure: true });
  }

  async getDatabaseStats() {
    return this.request('/admin/database-stats');
  }

  async getDatabaseStatsByCompany() {
    return this.request('/admin/database-stats/by-company');
  }

  async getPlatformSecurity() {
    return this.request('/platform/security');
  }

  async updatePlatformSecurity(payload) {
    return this.request('/platform/security', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async unlockCompanyAdmin(id) {
    return this.request(`/companies/${id}/unlock-admin`, {
      method: 'POST',
    });
  }

  async resetCompanyAdmin(id) {
    return this.request(`/companies/${id}/reset-admin`, {
      method: 'POST',
    });
  }

  async impersonateCompanyAdmin(id) {
    return this.request(`/companies/${id}/impersonate`, {
      method: 'POST',
    });
  }

  async updateCompanyLabels(id, payload) {
    return this.request(`/companies/${id}/labels`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async getAuditLogs({ limit = 100, company = '', action = '', sort = 'date_desc' } = {}) {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (company) params.set('company', company);
    if (action) params.set('action', action);
    if (sort) params.set('sort', sort);
    return this.request(`/audit-logs?${params.toString()}`);
  }

  async getContactLeads(limit = 100) {
    return this.request(`/contact-leads?limit=${encodeURIComponent(String(limit))}`);
  }

  async updateContactLead(id, payload) {
    return this.request(`/contact-leads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
  }

  async deleteContactLead(id) {
    return this.request(`/contact-leads/${id}`, {
      method: 'DELETE'
    });
  }

  async getWebsiteAnalytics() {
    return this.request('/website-analytics');
  }

  async unlockUser(username) {
    return this.request(`/users/${encodeURIComponent(username)}/unlock`, {
      method: 'POST',
    });
  }

  async updateUserTimezone(id, timezone) {
    return this.request(`/users/${id}/timezone`, {
      method: 'PUT',
      body: JSON.stringify({ timezone }),
    });
  }

  // Stock Notes
  async saveStockNote(colorId, notes) {
    return this.request('/stock-notes', {
      method: 'POST',
      body: JSON.stringify({ 
        color_id: colorId, 
        notes 
      }),
    });
  }

  // Order Drafts
  async getOrderDrafts() {
    return this.request('/order-drafts');
  }

  async addOrderDraft(draft) {
    return this.request('/order-drafts', {
      method: 'POST',
      body: JSON.stringify(draft),
    });
  }

  async deleteOrderDraft(id) {
    return this.request(`/order-drafts/${id}`, {
      method: 'DELETE',
    });
  }

  // Settings
  async getSettings(options = {}) {
    return this.request('/settings', options);
  }

  async updateSettings(thresholds) {
    return this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify(thresholds),
    });
  }

  async sendTestEmail(to) {
    return this.request('/settings/test-email', {
      method: 'POST',
      body: JSON.stringify({ to }),
    });
  }

  async getNotifications(limit = 30) {
    return this.request(`/notifications?limit=${limit}`);
  }

  async markNotificationRead(id) {
    return this.request(`/notifications/${id}/read`, { method: 'POST' });
  }

  async markAllNotificationsRead() {
    return this.request('/notifications/read-all', { method: 'POST' });
  }

  // Products
  async getProducts(options = {}) {
    return this.request('/products', options);
  }

  async createProduct(product) {
    return this.request('/products', {
      method: 'POST',
      body: JSON.stringify(product),
    });
  }

  async updateProduct(id, product) {
    return this.request(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(product),
    });
  }

  async updateProductNotes(id, notes) {
    return this.request(`/products/${id}/notes`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    });
  }

  async deleteProduct(id) {
    return this.request(`/products/${id}`, {
      method: 'DELETE',
    });
  }

  async updateMyPassword(currentPassword, newPassword) {
    // This endpoint re-authenticates the *current* password, so a 401 here means
    // "current password incorrect" — a credential error, NOT an expired session.
    // Skip the token-refresh/auto-logout path (same as login) so a wrong current
    // password surfaces as an inline error instead of kicking the user to /login.
    const data = await this.request('/users/me/password', {
      method: 'POST',
      skipAuthRefresh: true,
      suppressAuthFailure: true,
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    if (data?.token || data?.refresh_token || data?.user) {
      this.setAuthSession({
        token: data.token,
        refreshToken: data.refresh_token,
        user: data.user
      });
    }
    return data;
  }

  async getOnboardingMe(options = {}) {
    return this.request('/onboarding/me', options);
  }

  async updateOnboardingMe(payload) {
    return this.request('/onboarding/me', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async resetOnboardingMe() {
    return this.request('/onboarding/me/reset', {
      method: 'POST',
    });
  }

  // Order Number
  async getOrderNumber() {
    return this.request('/order-number');
  }

  async reserveOrderNumber() {
    return this.request('/order-number/reserve', {
      method: 'POST',
    });
  }

  // Orders
  async getOrders(options = {}) {
    return this.request('/orders', options);
  }

  async createOrder(order) {
    return this.request('/orders', {
      method: 'POST',
      body: JSON.stringify(order),
    });
  }

  async updateOrderProcessInstructions(id, process_instructions) {
    return this.request(`/orders/${id}/process-instructions`, {
      method: 'PATCH',
      body: JSON.stringify({ process_instructions }),
    });
  }

  async updateOrderNotes(id, notes) {
    return this.request(`/orders/${id}/notes`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    });
  }

  async transitionOrderPipelineStage(id, action, options = {}) {
    return this.request(`/orders/${id}/pipeline-transition`, {
      method: 'POST',
      body: JSON.stringify({ action, ...options }),
    });
  }

  async dispatchOrder(id, { quantity, note } = {}) {
    return this.request(`/orders/${id}/dispatch`, {
      method: 'POST',
      body: JSON.stringify({ quantity, note }),
    });
  }

  async getOrderCostBreakdown(id) {
    return this.request(`/orders/${id}/cost-breakdown`);
  }

  async updateOrderCostComponents(id, payload) {
    return this.request(`/orders/${id}/cost-components`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async getOrderExpenses(id) {
    return this.request(`/orders/${id}/expenses`);
  }

  async addOrderExpense(id, payload) {
    return this.request(`/orders/${id}/expenses`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteOrderExpense(orderId, expenseId) {
    return this.request(`/orders/${orderId}/expenses/${expenseId}`, {
      method: 'DELETE',
    });
  }


  async getOrderPipelineHistory(id) {
    return this.request(`/orders/${id}/pipeline-history`);
  }

}

const apiService = new ApiService();

export default apiService;
